import React, { useState, useEffect } from 'react';
import { AlertCircle } from 'lucide-react';
import { tutorService } from '../services/tutorService';

const QualificationSelector = ({ value = { boards: [], classes: [], subjects: [] }, onChange }) => {
  const [boards, setBoards] = useState([]);
  const [classes, setClasses] = useState([]);
  const [availableSubjects, setAvailableSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingSubjects, setLoadingSubjects] = useState(false);

  useEffect(() => {
    loadMasterData();
  }, []);

  useEffect(() => {
    // Load subjects when classes change
    if (value.classes && value.classes.length > 0) {
      loadSubjectsForClasses(value.classes);
    } else {
      setAvailableSubjects([]);
    }
  }, [value.classes]);

  const loadMasterData = async () => {
    try {
      setLoading(true);
      const [boardsData, classesData] = await Promise.all([
        tutorService.getBoards(),
        tutorService.getClasses(),
      ]);
      
      setBoards(boardsData.data || []);
      setClasses(classesData.data || []);
    } catch (error) {
      console.error('Failed to load master data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSubjectsForClasses = async (classIds) => {
    try {
      setLoadingSubjects(true);
      
      // Fetch subjects for each selected class
      const subjectPromises = classIds.map(classId => 
        tutorService.getClassSubjects(classId)
      );
      
      const subjectResponses = await Promise.all(subjectPromises);
      
      // Combine all subjects and remove duplicates
      const allSubjects = subjectResponses.flatMap(response => response.data || []);
      
      // Remove duplicates based on subject ID
      const uniqueSubjects = allSubjects.reduce((acc, subject) => {
        if (!acc.find(s => s.id === subject.id)) {
          acc.push(subject);
        }
        return acc;
      }, []);
      
      // Sort by sort_order and name
      uniqueSubjects.sort((a, b) => {
        if (a.sort_order !== b.sort_order) {
          return a.sort_order - b.sort_order;
        }
        return a.name.localeCompare(b.name);
      });
      
      setAvailableSubjects(uniqueSubjects);
      
      // Remove any selected subjects that are no longer available
      const availableSubjectIds = uniqueSubjects.map(s => s.id);
      const validSelectedSubjects = value.subjects.filter(subjectId => 
        availableSubjectIds.includes(subjectId)
      );
      
      if (validSelectedSubjects.length !== value.subjects.length) {
        onChange({ ...value, subjects: validSelectedSubjects });
      }
    } catch (error) {
      console.error('Failed to load subjects for classes:', error);
      setAvailableSubjects([]);
    } finally {
      setLoadingSubjects(false);
    }
  };

  const handleMultiSelect = (field, itemId) => {
    const currentValues = value[field] || [];
    const newValues = currentValues.includes(itemId)
      ? currentValues.filter(id => id !== itemId)
      : [...currentValues, itemId];
    
    onChange({ ...value, [field]: newValues });
  };

  const hasLowerClasses = () => {
    // Check if any selected class is LKG, UKG, or Class 1-5
    const lowerClassNames = ['LKG', 'UKG', 'Class 1', 'Class 2', 'Class 3', 'Class 4', 'Class 5'];
    return value.classes?.some(classId => {
      const cls = classes.find(c => c.id === classId);
      return cls && lowerClassNames.includes(cls.name);
    });
  };

  if (loading) {
    return <div className="text-center py-4">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Boards Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Boards You Can Teach * (Select multiple)
        </label>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {boards.map((board) => (
            <button
              key={board.id}
              type="button"
              onClick={() => handleMultiSelect('boards', board.id)}
              className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition-all duration-200 ${
                value.boards?.includes(board.id)
                  ? 'bg-gradient-to-r from-blue-600 to-teal-500 border-transparent text-white shadow-md'
                  : 'border-gray-200 text-gray-700 hover:border-blue-300 hover:bg-blue-50'
              }`}
            >
              {board.name}
            </button>
          ))}
        </div>
      </div>

      {/* Classes Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Classes/Grade Levels You Can Teach * (Select multiple)
        </label>
        <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {classes.map((cls) => (
            <button
              key={cls.id}
              type="button"
              onClick={() => handleMultiSelect('classes', cls.id)}
              className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition-all duration-200 ${
                value.classes?.includes(cls.id)
                  ? 'bg-gradient-to-r from-blue-600 to-teal-500 border-transparent text-white shadow-md'
                  : 'border-gray-200 text-gray-700 hover:border-blue-300 hover:bg-blue-50'
              }`}
            >
              {cls.name}
            </button>
          ))}
        </div>
      </div>

      {/* Important Notice for Lower Classes */}
      {hasLowerClasses() && (
        <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-900 mb-1">
                Important: Teaching Requirements for Lower Classes
              </p>
              <p className="text-sm text-amber-800">
                Teachers for classes below 5th standard (LKG, UKG, Class 1-5) must be able to teach <strong>all subjects</strong> including English, Hindi, Mathematics, General Science, Environmental Studies, and Social Studies.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Subjects Selection - Only show after classes are selected */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Subjects You Can Teach * (Select multiple)
        </label>
        
        {value.classes?.length === 0 ? (
          <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
            <p className="text-gray-500">Please select classes first to see available subjects</p>
          </div>
        ) : loadingSubjects ? (
          <div className="text-center py-8">
            <p className="text-gray-500">Loading subjects for selected classes...</p>
          </div>
        ) : availableSubjects.length === 0 ? (
          <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
            <p className="text-gray-500">No subjects available for selected classes</p>
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-600 mb-3">
              {hasLowerClasses() 
                ? 'For classes 6 and above, select the subjects you can teach. For lower classes, you must teach all subjects.'
                : 'Select the subjects you are qualified to teach for the selected classes.'
              }
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {availableSubjects.map((subject) => (
                <button
                  key={subject.id}
                  type="button"
                  onClick={() => handleMultiSelect('subjects', subject.id)}
                  className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition-all duration-200 ${
                    value.subjects?.includes(subject.id)
                      ? 'bg-gradient-to-r from-blue-600 to-teal-500 border-transparent text-white shadow-md'
                      : 'border-gray-200 text-gray-700 hover:border-blue-300 hover:bg-blue-50'
                  }`}
                >
                  {subject.name}
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Summary */}
      {(value.boards?.length > 0 || value.classes?.length > 0 || value.subjects?.length > 0) && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-blue-900 mb-2">Your Selection Summary:</h4>
          <div className="text-sm text-blue-800 space-y-1">
            <p><strong>Boards:</strong> {value.boards?.length || 0} selected</p>
            <p><strong>Classes:</strong> {value.classes?.length || 0} selected</p>
            <p><strong>Subjects:</strong> {value.subjects?.length || 0} selected</p>
            <p className="text-xs text-blue-600 mt-2">
              The system will create all possible combinations of your selected boards, classes, and subjects.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default QualificationSelector;
